import React from 'react';
import { Package, Droplets, Factory, CheckCircle2, Download, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const RefinedOilsDetail = ({ product }) => {
  if (!product.oilTypes || !product.packaging) return null;

  return (
    <div className="mt-8 space-y-10">
      {/* Oil Varieties */}
      <div>
        <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Droplets className="w-6 h-6 mr-2 text-amber-600" />
          Available Oil Varieties
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          {product.oilTypes.map((oil) => (
            <Card key={oil.name} className="border-2 hover:border-amber-400 transition-colors">
              <CardContent className="p-4">
                <h5 className="font-semibold text-gray-900 mb-1">{oil.name}</h5>
                <p className="text-sm text-gray-600">{oil.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Bulk Packaging */}
      <div>
        <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Factory className="w-6 h-6 mr-2 text-amber-600" />
          Bulk Packaging Solutions
        </h4>
        <div className="grid md:grid-cols-2 gap-6">
          {product.packaging.bulk.map((pack) => (
            <Card key={pack.type} className="overflow-hidden border-2 hover:shadow-xl transition-all">
              <div className="relative h-64">
                <img 
                  src={pack.image}
                  alt={pack.type}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                  <h5 className="text-xl font-bold text-gray-900">{pack.type}</h5>
                </div>
              </div>
              <CardContent className="p-5">
                <p className="text-gray-700 mb-2">{pack.description}</p>
                <p className="text-sm font-semibold text-amber-600">
                  Capacity: {pack.capacity}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Retail Packaging */}
      <div>
        <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Package className="w-6 h-6 mr-2 text-amber-600" />
          Retail Packaging - Sunterra Brand
        </h4>
        
        {/* Commercial Style Product Showcase */}
        <Card className="overflow-hidden border-2 shadow-2xl">
          <div className="relative bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-12">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-32 h-32 bg-amber-400 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-40 h-40 bg-yellow-400 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-orange-300 rounded-full blur-3xl"></div>
            </div>

            {/* Brand Title */}
            <div className="text-center mb-8 relative z-10">
              <div className="inline-block mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/r8ekj8u6_sunterra%20logo.png"
                  alt="Sunterra Logo"
                  className="h-20 md:h-24 w-auto mx-auto"
                />
              </div>
              <p className="text-xl text-gray-700">Premium Quality Edible Oils</p>
            </div>

            {/* Products Display */}
            <div className="grid md:grid-cols-3 gap-8 relative z-10">
              {/* Small size PET Bottles */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="relative h-64 mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/uz6h5if1_1%20liter%20pet.jpeg"
                    alt="Small size PET bottles"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Small size PET bottles</h5>
                  <p className="text-sm text-gray-600 mb-3">Perfect for everyday cooking</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      0.5L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      0.9L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      1L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      1.5L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      1.8L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      2L
                    </span>
                    <span className="inline-block px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      3L
                    </span>
                  </div>
                </div>
              </div>

              {/* Big size PET Bottles */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="relative h-64 mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/ffaqud62_5%20liter%20pet%20.jpeg"
                    alt="Big size PET bottles"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Big size PET bottles</h5>
                  <p className="text-sm text-gray-600 mb-3">Economy size for families</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-3">
                    <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      4.5L
                    </span>
                    <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      5L
                    </span>
                  </div>
                </div>
              </div>

              {/* Metal Tins */}
              <div className="bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2">
                <div className="relative h-64 mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/8751wjyb_5%20liter%20tins.jpeg"
                    alt="Metal Tins"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="text-center">
                  <h5 className="text-xl font-bold text-gray-900 mb-2">Metal Tins</h5>
                  <p className="text-sm text-gray-600 mb-3">Premium packaging with extended shelf life</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-3">
                    <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      5L
                    </span>
                    <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      18L
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Features */}
            <div className="mt-10 bg-white/60 backdrop-blur-sm rounded-xl p-6 relative z-10">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-amber-600 mb-2">Premium Quality</div>
                  <p className="text-sm text-gray-600">Refined for purity & taste</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600 mb-2">Multiple Sizes</div>
                  <p className="text-sm text-gray-600">0.5L to 18L options available</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600 mb-2">Trusted Brand</div>
                  <p className="text-sm text-gray-600">Quality you can rely on</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Brand Highlight */}
      <Card className="bg-gradient-to-br from-amber-50 to-green-50 border-2 border-amber-300">
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/r8ekj8u6_sunterra%20logo.png"
                alt="Sunterra Brand"
                className="h-16 md:h-20 w-auto"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Sunterra Brand</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our Sunterra brand represents our commitment to quality and excellence in edible oils. 
                Whether you need bulk quantities for industrial processing or retail packaging for your 
                store shelves, Sunterra delivers consistent quality you can trust.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PDF Download Section */}
      <div className="mt-10">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 shadow-xl">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
                  <FileText className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Refined Sunflower Oil Specifications
                </h3>
                <p className="text-gray-700 mb-4">
                  Download detailed technical specifications and quality parameters for Sunterra Refined Sunflower Oil
                </p>
                <a
                  href="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/p5diu3vc_Refined_Sunflower_Oil_Spec%20Sunterra.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Specification Sheet (PDF)
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OEM / Private Label Section */}
      <div className="mt-10">
        <Card className="overflow-hidden border-2 border-blue-300 shadow-2xl">
          {/* Image on Top - Full Width */}
          <div className="relative h-[500px] w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_eyco-refinery/artifacts/px60002l_your%20label.jpeg"
              alt="OEM Private Label"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            <div className="absolute bottom-8 left-8">
              <h3 className="text-4xl font-bold text-white mb-2">
                OEM & Private Label Services
              </h3>
              <p className="text-xl text-white/90">
                Build Your Own Brand with EYCO
              </p>
            </div>
          </div>
          
          {/* Content Below */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                EYCO offers comprehensive OEM (Original Equipment Manufacturer) and private label 
                services for customers looking to build their own brand. We can customize our premium 
                edible oils with your brand identity, labels, and packaging specifications.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Custom Branding</h4>
                      <p className="text-sm text-gray-600">Label design and brand identity tailored to your needs</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Flexible Packaging</h4>
                      <p className="text-sm text-gray-600">PET bottles, metal tins, or bulk packaging options</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Multiple Varieties</h4>
                      <p className="text-sm text-gray-600">All oil types available for your brand</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Quality Assured</h4>
                      <p className="text-sm text-gray-600">Full certifications and quality control included</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-600 text-white rounded-xl p-6 text-center">
                <p className="text-xl font-bold mb-2">
                  Ready to Launch Your Own Brand?
                </p>
                <p className="text-blue-100">
                  Contact us today to discuss MOQ and customization options
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RefinedOilsDetail;
